import { supabase } from '../supabase'
import { normalizeUsername } from '../credentials'
import type { Role } from '../../types'

/**
 * Staff management, via the Edge Function that holds the service-role key.
 *
 * None of this can happen in the browser: creating a login, changing someone
 * else's password and deleting an account all need a key that bypasses every
 * policy in the database. The function checks who is asking before it acts,
 * see supabase/functions/staff/index.ts.
 *
 * The signatures match the Firebase versions exactly so the settings screen
 * does not have to know which stack it is on.
 */

async function call(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')

  const { data, error } = await supabase.functions.invoke('staff', { body })

  if (error) {
    // The function's own message is the useful one, "Bu kullanıcı adı
    // alınmış" tells the owner what to do; "FunctionsHttpError" does not.
    const detail = (await error.context?.json?.().catch(() => null)) as
      | { error?: string }
      | null
    throw new Error(detail?.error ?? 'İşlem tamamlanamadı.')
  }
  if (data?.error) throw new Error(String(data.error))
  return (data ?? {}) as Record<string, unknown>
}

/** Checked here as well as on the server: a clear message beats a 400. */
function validate(username: string, pin: string): string {
  const clean = normalizeUsername(username)
  if (!clean) throw new Error('Kullanıcı adı geçersiz.')
  if (!/^\d{4,8}$/.test(pin)) throw new Error('PIN 4-8 rakam olmalı.')
  return clean
}

export async function createStaff(
  tenantId: string,
  input: { username: string; pin: string; role: Exclude<Role, 'owner'>; displayName?: string; station?: string },
): Promise<void> {
  const username = validate(input.username, input.pin)
  await call({
    action: 'create',
    tenantId,
    username,
    pin: input.pin,
    role: input.role,
    displayName: input.displayName ?? '',
    // Mutfak personelinin tezgahı; boş = bütün tezgahlar.
    ...(input.role === 'kitchen' && input.station ? { perms: { station: input.station } } : {}),
  })
}

export async function removeStaff(
  tenantId: string,
  uid: string,
  // Accepted for signature parity with the Firebase version, which needed it
  // to clear the username index. There is no index here: the address follows
  // from the username, so nothing has to be cleaned up.
  _username?: string,
): Promise<void> {
  await call({ action: 'remove', tenantId, targetId: uid })
}

/**
 * Changes someone's username, PIN or display name.
 *
 * Updates the account in place. The Firebase version could not do that from a
 * browser and had to create a new account at a suffixed address each time
 * (kasa+2@, kasa+3@), leaving the old one behind for ever; that mechanism,
 * and the index tracking which address was current, is gone.
 */
export async function resetStaffCredentials(
  tenantId: string,
  person: { uid: string; username: string; displayName?: string; role: Exclude<Role, 'owner'> },
  next: { username: string; pin: string },
): Promise<void> {
  const username = validate(next.username, next.pin)
  await call({
    action: 'reset',
    tenantId,
    targetId: person.uid,
    username,
    pin: next.pin,
    displayName: person.displayName,
  })
}
